import type { LucideIcon } from "lucide-react";
import {
  Beer,
  CakeSlice,
  Coffee,
  Cookie,
  Croissant,
  Droplet,
  Gift,
  GlassWater,
  IceCream,
  Leaf,
  Pizza,
  Sandwich,
  Smile,
  Soup,
  Star,
  Utensils,
} from "lucide-react";

export interface Category {
  id: string;
  label: string;
  icon: LucideIcon;
  image: string;
}

const unsplash = (id: string) =>
  `https://images.unsplash.com/${id}?q=80&w=600&auto=format&fit=crop`;

export const categories: Category[] = [
  {
    id: "favorites",
    label: "Favorites",
    icon: Star,
    image: unsplash("photo-1504674900247-0877df9cc836"),
  },
  {
    id: "coffee",
    label: "Coffee",
    icon: Coffee,
    image: unsplash("photo-1509042239860-f550ce710b93"),
  },
  { id: "tea", label: "Tea", icon: Leaf, image: unsplash("photo-1544787219-7f47ccb76574") },
  {
    id: "cold-drinks",
    label: "Cold Drinks",
    icon: GlassWater,
    image: unsplash("photo-1437418747212-8d9709afab22"),
  },
  {
    id: "bakery",
    label: "Bakery",
    icon: Croissant,
    image: unsplash("photo-1509440159596-0249088772ff"),
  },
  {
    id: "desserts",
    label: "Desserts",
    icon: CakeSlice,
    image: unsplash("photo-1551024506-0bccd828d307"),
  },
  {
    id: "sandwiches",
    label: "Sandwiches",
    icon: Sandwich,
    image: unsplash("photo-1528735602780-2552fd46c7af"),
  },
  { id: "pizza", label: "Pizza", icon: Pizza, image: unsplash("photo-1513104890138-7c749659a591") },
  { id: "pasta", label: "Pasta", icon: Soup, image: unsplash("photo-1621996346565-e3dbc646d9a9") },
  { id: "soups", label: "Soups", icon: Soup, image: unsplash("photo-1547592166-23ac45744acd") },
  {
    id: "salads",
    label: "Salads",
    icon: Leaf,
    image: unsplash("photo-1512621776951-a57141f2eefd"),
  },
  {
    id: "smoothies",
    label: "Smoothies",
    icon: Droplet,
    image: unsplash("photo-1505252585461-04db1eb84625"),
  },
  {
    id: "sides",
    label: "Sides",
    icon: Utensils,
    image: unsplash("photo-1573080496219-bb080dd4f877"),
  },
  {
    id: "snacks",
    label: "Snacks",
    icon: Cookie,
    image: unsplash("photo-1566478989037-eec170784d0b"),
  },
  {
    id: "ice-cream",
    label: "Ice Cream",
    icon: IceCream,
    image: unsplash("photo-1501443762994-82bd5dace89a"),
  },
  {
    id: "breakfast",
    label: "Breakfast",
    icon: Croissant,
    image: unsplash("photo-1533089860892-a7c6f0a88666"),
  },
  { id: "kids", label: "Kids Menu", icon: Smile, image: unsplash("photo-1561758033-d89a9ad46330") },
  {
    id: "beer-wine",
    label: "Beer & Wine",
    icon: Beer,
    image: unsplash("photo-1510812431401-41d2bd2722f3"),
  },
  {
    id: "cocktails",
    label: "Cocktails",
    icon: GlassWater,
    image: unsplash("photo-1514362545857-3bc16c4c7d1b"),
  },
  {
    id: "merch",
    label: "Merchandise",
    icon: Gift,
    image: unsplash("photo-1514228742587-6b1558fcca3d"),
  },
];

export interface Product {
  id: string;
  name: string;
  price: number;
  icon: LucideIcon;
  iconClassName: string;
  categoryId: string;
  isFavorite?: boolean;
  image?: string;
  description?: string;
  descriptionEn?: string;
  isAvailable?: boolean; // undefined = stokta var
  calories?: number;
  allergens?: string[];
  allergensEn?: string[];
}

export const products: Product[] = [
  // COFFEE
  {
    id: "espresso",
    name: "Espresso",
    price: 2.5,
    icon: Coffee,
    iconClassName: "bg-amber-100 text-amber-700",
    categoryId: "coffee",
    isFavorite: true,
    image: unsplash("photo-1510591509098-f4fdc6d0ff04"),
    description: "Yoğun aromalı, taze öğütülmüş çekirdeklerden tek shot klasik espresso.",
    descriptionEn: "A classic single shot of intense espresso from freshly ground beans.",
    calories: 5,
  },
  {
    id: "cappuccino",
    name: "Cappuccino",
    price: 3.5,
    icon: Coffee,
    iconClassName: "bg-orange-100 text-orange-700",
    categoryId: "coffee",
    isFavorite: true,
    image: unsplash("photo-1572442388796-11668a67e53d"),
    description: "Kadifemsi süt köpüğü ve dengeli espresso'nun buluştuğu İtalyan klasiği.",
    descriptionEn: "The Italian classic where velvety milk foam meets balanced espresso.",
    calories: 120,
    allergens: ["Süt"],
    allergensEn: ["Milk"],
  },
  {
    id: "latte",
    name: "Latte",
    price: 3.75,
    icon: Coffee,
    iconClassName: "bg-yellow-100 text-yellow-700",
    categoryId: "coffee",
    isFavorite: true,
    image: unsplash("photo-1561047029-3000c68339ca"),
    description: "Bol buğulanmış süt ve yumuşak içimli espresso, latte art ile servis edilir.",
    descriptionEn: "Steamed milk and smooth espresso, served with latte art.",
    calories: 150,
    allergens: ["Süt"],
    allergensEn: ["Milk"],
  },
  {
    id: "americano",
    name: "Americano",
    price: 3.0,
    icon: Coffee,
    iconClassName: "bg-stone-100 text-stone-700",
    categoryId: "coffee",
    isFavorite: true,
    image: unsplash("photo-1551030173-122aabc4489c"),
    description: "Sıcak su ile yumuşatılmış espresso; sade kahve severlerin tercihi.",
    descriptionEn: "Espresso mellowed with hot water; a favorite of black coffee lovers.",
    calories: 10,
  },
  {
    id: "mocha",
    name: "Mocha",
    price: 4.0,
    icon: Coffee,
    iconClassName: "bg-amber-100 text-amber-800",
    categoryId: "coffee",
  },
  {
    id: "flat-white",
    name: "Flat White",
    price: 3.75,
    icon: Coffee,
    iconClassName: "bg-orange-100 text-orange-800",
    categoryId: "coffee",
  },
  {
    id: "macchiato",
    name: "Macchiato",
    price: 3.25,
    icon: Coffee,
    iconClassName: "bg-stone-100 text-stone-800",
    categoryId: "coffee",
  },
  {
    id: "cortado",
    name: "Cortado",
    price: 3.25,
    icon: Coffee,
    iconClassName: "bg-yellow-100 text-amber-800",
    categoryId: "coffee",
  },
  {
    id: "cold-brew",
    name: "Cold Brew",
    price: 3.5,
    icon: GlassWater,
    iconClassName: "bg-amber-50 text-amber-900",
    categoryId: "coffee",
    isAvailable: false,
  },
  {
    id: "turkish-coffee",
    name: "Turkish Coffee",
    price: 3.0,
    icon: Coffee,
    iconClassName: "bg-stone-200 text-stone-900",
    categoryId: "coffee",
  },

  // TEA
  {
    id: "english-breakfast",
    name: "English Breakfast",
    price: 2.75,
    icon: Leaf,
    iconClassName: "bg-emerald-100 text-emerald-700",
    categoryId: "tea",
  },
  {
    id: "earl-grey",
    name: "Earl Grey",
    price: 2.75,
    icon: Leaf,
    iconClassName: "bg-emerald-50 text-emerald-800",
    categoryId: "tea",
  },
  {
    id: "green-tea",
    name: "Green Tea",
    price: 2.5,
    icon: Leaf,
    iconClassName: "bg-green-100 text-green-700",
    categoryId: "tea",
    isFavorite: true,
    image: unsplash("photo-1627435601361-ec25f5b1d0e5"),
    description: "Antioksidan deposu, taze demlenmiş hafif içimli yeşil çay.",
    descriptionEn: "Freshly brewed light green tea, packed with antioxidants.",
    calories: 0,
  },
  {
    id: "chamomile",
    name: "Chamomile",
    price: 2.5,
    icon: Leaf,
    iconClassName: "bg-yellow-50 text-yellow-700",
    categoryId: "tea",
  },
  {
    id: "jasmine-tea",
    name: "Jasmine Green",
    price: 2.75,
    icon: Leaf,
    iconClassName: "bg-green-50 text-green-800",
    categoryId: "tea",
  },
  {
    id: "matcha-latte",
    name: "Matcha Latte",
    price: 4.25,
    icon: Leaf,
    iconClassName: "bg-emerald-100 text-emerald-800",
    categoryId: "tea",
    image: unsplash("photo-1536256263959-770b48d82b0a"),
    description: "Seremonik matcha tozu ve buğulanmış sütle hazırlanan canlandırıcı latte.",
    descriptionEn: "An energizing latte of ceremonial matcha whisked with steamed milk.",
    calories: 180,
    allergens: ["Süt"],
    allergensEn: ["Milk"],
  },
  {
    id: "chai-latte",
    name: "Chai Latte",
    price: 4.0,
    icon: Coffee,
    iconClassName: "bg-amber-100 text-amber-900",
    categoryId: "tea",
  },
  {
    id: "herbal-blend",
    name: "Herbal Tea",
    price: 2.75,
    icon: Leaf,
    iconClassName: "bg-teal-50 text-teal-800",
    categoryId: "tea",
  },

  // COLD DRINKS
  {
    id: "lemonade",
    name: "Lemonade",
    price: 3.0,
    icon: GlassWater,
    iconClassName: "bg-lime-100 text-lime-700",
    categoryId: "cold-drinks",
    isFavorite: true,
    image: unsplash("photo-1523677011781-c91d1bbe2f9e"),
    description: "Taze sıkılmış limon ve nane yaprakları ile ev yapımı serinletici limonata.",
    descriptionEn: "Homemade refreshing lemonade with fresh-squeezed lemon and mint.",
    calories: 110,
  },
  {
    id: "iced-peach-tea",
    name: "Iced Peach Tea",
    price: 3.0,
    icon: GlassWater,
    iconClassName: "bg-orange-100 text-orange-700",
    categoryId: "cold-drinks",
  },
  {
    id: "iced-lemon-tea",
    name: "Iced Lemon Tea",
    price: 3.0,
    icon: GlassWater,
    iconClassName: "bg-yellow-100 text-yellow-700",
    categoryId: "cold-drinks",
  },
  {
    id: "bottled-water",
    name: "Bottled Water",
    price: 2.0,
    icon: Droplet,
    iconClassName: "bg-blue-100 text-blue-700",
    categoryId: "cold-drinks",
    isFavorite: true,
  },
  {
    id: "sparkling-water",
    name: "Sparkling Water",
    price: 2.5,
    icon: Droplet,
    iconClassName: "bg-sky-100 text-sky-700",
    categoryId: "cold-drinks",
  },
  {
    id: "cola",
    name: "Coca Cola",
    price: 2.5,
    icon: GlassWater,
    iconClassName: "bg-red-100 text-red-700",
    categoryId: "cold-drinks",
  },
  {
    id: "sprite",
    name: "Sprite",
    price: 2.5,
    icon: GlassWater,
    iconClassName: "bg-green-100 text-green-700",
    categoryId: "cold-drinks",
  },
  {
    id: "orange-juice",
    name: "Orange Juice",
    price: 3.5,
    icon: GlassWater,
    iconClassName: "bg-orange-100 text-orange-800",
    categoryId: "cold-drinks",
  },

  // BAKERY
  {
    id: "croissant",
    name: "Croissant",
    price: 2.75,
    icon: Croissant,
    iconClassName: "bg-yellow-100 text-yellow-800",
    categoryId: "bakery",
    isFavorite: true,
    image: unsplash("photo-1555507036-ab1f4038808a"),
    description: "Kat kat açılan hamuru ve bol tereyağıyla her sabah taze pişer.",
    descriptionEn: "Flaky, buttery layers baked fresh every morning.",
    calories: 270,
    allergens: ["Gluten", "Süt", "Yumurta"],
    allergensEn: ["Gluten", "Milk", "Egg"],
  },
  {
    id: "chocolate-croissant",
    name: "Pain au Chocolat",
    price: 3.25,
    icon: Croissant,
    iconClassName: "bg-amber-100 text-amber-900",
    categoryId: "bakery",
  },
  {
    id: "cinnamon-roll",
    name: "Cinnamon Roll",
    price: 3.5,
    icon: Croissant,
    iconClassName: "bg-orange-100 text-orange-800",
    categoryId: "bakery",
  },
  {
    id: "blueberry-muffin",
    name: "Blueberry Muffin",
    price: 3.0,
    icon: Cookie,
    iconClassName: "bg-blue-100 text-blue-800",
    categoryId: "bakery",
  },
  {
    id: "cookie",
    name: "Choc Chip Cookie",
    price: 2.0,
    icon: Cookie,
    iconClassName: "bg-stone-200 text-stone-800",
    categoryId: "bakery",
  },
  {
    id: "scone",
    name: "English Scone",
    price: 2.75,
    icon: Cookie,
    iconClassName: "bg-orange-50 text-orange-700",
    categoryId: "bakery",
  },
  {
    id: "bagel",
    name: "Bagel w/ Cream Cheese",
    price: 3.5,
    icon: Sandwich,
    iconClassName: "bg-yellow-50 text-yellow-800",
    categoryId: "bakery",
  },

  // DESSERTS
  {
    id: "cheesecake",
    name: "Cheesecake",
    price: 4.5,
    icon: CakeSlice,
    iconClassName: "bg-pink-100 text-pink-700",
    categoryId: "desserts",
    isFavorite: true,
    image: unsplash("photo-1524351199679-46cddf530c04"),
    description: "Fırında yavaş pişirilmiş kremamsı cheesecake, taze meyve sosu ile.",
    descriptionEn: "Slow-baked creamy cheesecake, served with fresh fruit coulis.",
    calories: 450,
    allergens: ["Süt", "Gluten", "Yumurta"],
    allergensEn: ["Milk", "Gluten", "Egg"],
  },
  {
    id: "tiramisu",
    name: "Tiramisu",
    price: 4.75,
    icon: CakeSlice,
    iconClassName: "bg-amber-100 text-amber-900",
    categoryId: "desserts",
    image: unsplash("photo-1571877227200-a0d98ea607e9"),
    description: "Espresso emdirilmiş kedi dili ve mascarpone kremasıyla İtalyan klasiği.",
    descriptionEn: "The Italian classic with espresso-soaked ladyfingers and mascarpone.",
    calories: 420,
    allergens: ["Süt", "Gluten", "Yumurta"],
    allergensEn: ["Milk", "Gluten", "Egg"],
  },
  {
    id: "brownie",
    name: "Brownie",
    price: 3.25,
    icon: Cookie,
    iconClassName: "bg-stone-200 text-stone-800",
    categoryId: "desserts",
    isFavorite: true,
    image: unsplash("photo-1606313564200-e75d5e30476c"),
    description: "Dışı çıtır içi akışkan, yoğun çikolatalı el yapımı brownie.",
    descriptionEn: "Handmade brownie, crisp outside and gooey inside, rich in chocolate.",
    calories: 390,
    allergens: ["Gluten", "Süt", "Yumurta"],
    allergensEn: ["Gluten", "Milk", "Egg"],
  },
  {
    id: "apple-pie",
    name: "Apple Pie",
    price: 4.0,
    icon: CakeSlice,
    iconClassName: "bg-yellow-100 text-yellow-800",
    categoryId: "desserts",
  },
  {
    id: "carrot-cake",
    name: "Carrot Cake",
    price: 4.25,
    icon: CakeSlice,
    iconClassName: "bg-orange-100 text-orange-800",
    categoryId: "desserts",
  },
  {
    id: "fudge-cake",
    name: "Choc Fudge Cake",
    price: 4.5,
    icon: CakeSlice,
    iconClassName: "bg-rose-100 text-rose-800",
    categoryId: "desserts",
  },
  {
    id: "macarons",
    name: "Macaron Set",
    price: 6.0,
    icon: Cookie,
    iconClassName: "bg-pink-100 text-pink-600",
    categoryId: "desserts",
  },

  // SANDWICHES
  {
    id: "turkey-cheese",
    name: "Turkey & Cheese",
    price: 5.5,
    icon: Sandwich,
    iconClassName: "bg-orange-100 text-orange-800",
    categoryId: "sandwiches",
  },
  {
    id: "caprese-panini",
    name: "Caprese Panini",
    price: 5.75,
    icon: Sandwich,
    iconClassName: "bg-emerald-100 text-emerald-800",
    categoryId: "sandwiches",
  },
  {
    id: "chicken-pesto",
    name: "Chicken Pesto",
    price: 6.25,
    icon: Sandwich,
    iconClassName: "bg-green-100 text-green-800",
    categoryId: "sandwiches",
  },
  {
    id: "club-sandwich",
    name: "Club Sandwich",
    price: 6.5,
    icon: Sandwich,
    iconClassName: "bg-amber-100 text-amber-800",
    categoryId: "sandwiches",
  },
  {
    id: "tuna-sandwich",
    name: "Tuna Salad Panini",
    price: 5.5,
    icon: Sandwich,
    iconClassName: "bg-blue-100 text-blue-800",
    categoryId: "sandwiches",
  },
  {
    id: "veggie-wrap",
    name: "Veggie Wrap",
    price: 5.25,
    icon: Sandwich,
    iconClassName: "bg-teal-100 text-teal-800",
    categoryId: "sandwiches",
  },

  // PIZZA
  {
    id: "margherita",
    name: "Margherita Pizza",
    price: 8.5,
    icon: Pizza,
    iconClassName: "bg-red-100 text-red-800",
    categoryId: "pizza",
    isFavorite: true,
    image: unsplash("photo-1574071318508-1cdbab80d002"),
    description: "San Marzano domates, taze mozzarella ve fesleğenle taş fırında pişer.",
    descriptionEn: "San Marzano tomatoes, fresh mozzarella and basil, stone-oven baked.",
    calories: 850,
    allergens: ["Gluten", "Süt"],
    allergensEn: ["Gluten", "Milk"],
  },
  {
    id: "pepperoni",
    name: "Pepperoni Pizza",
    price: 9.75,
    icon: Pizza,
    iconClassName: "bg-orange-100 text-orange-800",
    categoryId: "pizza",
    isFavorite: true,
    image: unsplash("photo-1628840042765-356cda07504e"),
    description: "Bol pepperoni dilimleri ve erimiş mozzarella ile çıtır hamur.",
    descriptionEn: "Crispy crust loaded with pepperoni slices and melted mozzarella.",
    calories: 980,
    allergens: ["Gluten", "Süt"],
    allergensEn: ["Gluten", "Milk"],
  },
  {
    id: "veggie-pizza",
    name: "Garden Veggie Pizza",
    price: 9.25,
    icon: Pizza,
    iconClassName: "bg-green-100 text-green-800",
    categoryId: "pizza",
  },
  {
    id: "bbq-chicken",
    name: "BBQ Chicken Pizza",
    price: 10.0,
    icon: Pizza,
    iconClassName: "bg-amber-100 text-amber-900",
    categoryId: "pizza",
  },

  // PASTA
  {
    id: "spaghetti-bolo",
    name: "Spaghetti Bolognese",
    price: 11.5,
    icon: Soup,
    iconClassName: "bg-red-50 text-red-900",
    categoryId: "pasta",
  },
  {
    id: "fettuccine-alfredo",
    name: "Fettuccine Alfredo",
    price: 11.0,
    icon: Soup,
    iconClassName: "bg-stone-100 text-stone-800",
    categoryId: "pasta",
  },
  {
    id: "penne-arrabbiata",
    name: "Penne Arrabbiata",
    price: 9.5,
    icon: Soup,
    iconClassName: "bg-rose-100 text-rose-800",
    categoryId: "pasta",
  },

  // SOUPS
  {
    id: "tomato-soup",
    name: "Creamy Tomato Soup",
    price: 4.5,
    icon: Soup,
    iconClassName: "bg-red-100 text-red-700",
    categoryId: "soups",
  },
  {
    id: "lentil-soup",
    name: "Traditional Lentil Soup",
    price: 4.5,
    icon: Soup,
    iconClassName: "bg-yellow-100 text-yellow-800",
    categoryId: "soups",
  },
  {
    id: "chicken-noodle",
    name: "Chicken Noodle Soup",
    price: 5.0,
    icon: Soup,
    iconClassName: "bg-amber-50 text-amber-800",
    categoryId: "soups",
  },

  // SALADS
  {
    id: "caesar-salad",
    name: "Caesar Salad",
    price: 6.5,
    icon: Leaf,
    iconClassName: "bg-green-100 text-green-800",
    categoryId: "salads",
    image: unsplash("photo-1550304943-4f24f54ddde9"),
    description: "Izgara tavuk, parmesan ve çıtır krutonlarla klasik Sezar salata.",
    descriptionEn: "Classic Caesar with grilled chicken, parmesan and crunchy croutons.",
    calories: 330,
    allergens: ["Süt", "Gluten", "Yumurta", "Balık"],
    allergensEn: ["Milk", "Gluten", "Egg", "Fish"],
  },
  {
    id: "mediterranean-salad",
    name: "Greek Salad",
    price: 6.75,
    icon: Leaf,
    iconClassName: "bg-emerald-50 text-emerald-800",
    categoryId: "salads",
  },
  {
    id: "quinoa-bowl",
    name: "Quinoa Salad Bowl",
    price: 7.25,
    icon: Leaf,
    iconClassName: "bg-teal-50 text-teal-800",
    categoryId: "salads",
  },

  // SMOOTHIES
  {
    id: "berry-blast",
    name: "Berry Blast Smoothie",
    price: 4.5,
    icon: Droplet,
    iconClassName: "bg-pink-100 text-pink-700",
    categoryId: "smoothies",
    image: unsplash("photo-1553530666-ba11a7da3888"),
    description: "Yaban mersini, çilek ve ahududunun yoğurtla buluştuğu doğal karışım.",
    descriptionEn: "A natural blend of blueberry, strawberry and raspberry with yogurt.",
    calories: 210,
    allergens: ["Süt"],
    allergensEn: ["Milk"],
  },
  {
    id: "green-detox",
    name: "Green Detox Juice",
    price: 4.75,
    icon: Droplet,
    iconClassName: "bg-green-100 text-green-700",
    categoryId: "smoothies",
  },
  {
    id: "mango-tango",
    name: "Mango Tango",
    price: 4.5,
    icon: Droplet,
    iconClassName: "bg-orange-100 text-orange-700",
    categoryId: "smoothies",
  },

  // SIDES
  {
    id: "french-fries",
    name: "French Fries",
    price: 3.5,
    icon: Utensils,
    iconClassName: "bg-yellow-100 text-yellow-700",
    categoryId: "sides",
  },
  {
    id: "onion-rings",
    name: "Onion Rings",
    price: 4.0,
    icon: Utensils,
    iconClassName: "bg-orange-100 text-orange-800",
    categoryId: "sides",
  },
  {
    id: "garlic-bread",
    name: "Garlic Bread (3 pcs)",
    price: 3.0,
    icon: Utensils,
    iconClassName: "bg-amber-100 text-amber-800",
    categoryId: "sides",
  },

  // SNACKS
  {
    id: "potato-chips",
    name: "Salted Potato Chips",
    price: 2.0,
    icon: Cookie,
    iconClassName: "bg-yellow-50 text-yellow-600",
    categoryId: "snacks",
  },
  {
    id: "roasted-nuts",
    name: "Mixed Roasted Nuts",
    price: 3.0,
    icon: Cookie,
    iconClassName: "bg-amber-100 text-amber-900",
    categoryId: "snacks",
  },
  {
    id: "popcorn",
    name: "Butter Popcorn",
    price: 2.5,
    icon: Cookie,
    iconClassName: "bg-yellow-100 text-yellow-800",
    categoryId: "snacks",
  },

  // ICE CREAM
  {
    id: "vanilla-scoop",
    name: "Vanilla Ice Cream",
    price: 2.5,
    icon: IceCream,
    iconClassName: "bg-stone-100 text-stone-700",
    categoryId: "ice-cream",
  },
  {
    id: "chocolate-scoop",
    name: "Chocolate Ice Cream",
    price: 2.5,
    icon: IceCream,
    iconClassName: "bg-amber-100 text-amber-900",
    categoryId: "ice-cream",
  },
  {
    id: "strawberry-scoop",
    name: "Strawberry Ice Cream",
    price: 2.5,
    icon: IceCream,
    iconClassName: "bg-pink-100 text-pink-700",
    categoryId: "ice-cream",
  },

  // BREAKFAST
  {
    id: "egg-toast",
    name: "Avocado & Egg Toast",
    price: 6.5,
    icon: Croissant,
    iconClassName: "bg-green-100 text-green-800",
    categoryId: "breakfast",
    image: unsplash("photo-1541532713592-79a0317b6b77"),
    description: "Ekşi mayalı ekmek üzerinde ezilmiş avokado ve haşlanmış yumurta.",
    descriptionEn: "Smashed avocado and poached egg on sourdough toast.",
    calories: 340,
    allergens: ["Gluten", "Yumurta"],
    allergensEn: ["Gluten", "Egg"],
  },
  {
    id: "pancakes",
    name: "Pancakes w/ Maple Syrup",
    price: 5.75,
    icon: Croissant,
    iconClassName: "bg-yellow-100 text-yellow-800",
    categoryId: "breakfast",
    image: unsplash("photo-1567620905732-2d1ec7ab7445"),
    description: "Akçaağaç şurubu ve tereyağı ile servis edilen kabarık pancake kulesi.",
    descriptionEn: "A fluffy pancake stack served with maple syrup and butter.",
    calories: 520,
    allergens: ["Gluten", "Süt", "Yumurta"],
    allergensEn: ["Gluten", "Milk", "Egg"],
  },
  {
    id: "oatmeal",
    name: "Warm Honey Oatmeal",
    price: 4.5,
    icon: Croissant,
    iconClassName: "bg-amber-50 text-amber-700",
    categoryId: "breakfast",
  },

  // KIDS MENU
  {
    id: "chicken-nuggets",
    name: "Kids Chicken Nuggets",
    price: 4.5,
    icon: Smile,
    iconClassName: "bg-yellow-100 text-yellow-800",
    categoryId: "kids",
  },
  {
    id: "mini-burger",
    name: "Kids Mini Burger",
    price: 5.0,
    icon: Smile,
    iconClassName: "bg-orange-100 text-orange-800",
    categoryId: "kids",
  },
  {
    id: "mac-cheese",
    name: "Kids Mac & Cheese",
    price: 4.75,
    icon: Smile,
    iconClassName: "bg-yellow-50 text-yellow-700",
    categoryId: "kids",
  },

  // BEER & WINE
  {
    id: "craft-beer",
    name: "Local Craft IPA",
    price: 5.5,
    icon: Beer,
    iconClassName: "bg-amber-100 text-amber-800",
    categoryId: "beer-wine",
  },
  {
    id: "red-wine",
    name: "Cabernet Sauvignon Glass",
    price: 7.0,
    icon: Beer,
    iconClassName: "bg-red-100 text-red-900",
    categoryId: "beer-wine",
  },
  {
    id: "white-wine",
    name: "Chardonnay Glass",
    price: 6.5,
    icon: Beer,
    iconClassName: "bg-yellow-50 text-yellow-700",
    categoryId: "beer-wine",
  },

  // COCKTAILS
  {
    id: "mojito",
    name: "Classic Mojito",
    price: 8.0,
    icon: GlassWater,
    iconClassName: "bg-emerald-100 text-emerald-800",
    categoryId: "cocktails",
    image: unsplash("photo-1551538827-9c037cb4f32a"),
    description: "Taze nane, misket limonu ve soda ile hazırlanan ferahlatıcı klasik.",
    descriptionEn: "The refreshing classic with fresh mint, lime and soda.",
    calories: 170,
  },
  {
    id: "margarita",
    name: "Classic Margarita",
    price: 8.5,
    icon: GlassWater,
    iconClassName: "bg-lime-100 text-lime-800",
    categoryId: "cocktails",
  },
  {
    id: "negroni",
    name: "Negroni",
    price: 9.0,
    icon: GlassWater,
    iconClassName: "bg-red-100 text-red-800",
    categoryId: "cocktails",
    isAvailable: false,
  },

  // MERCHANDISE
  {
    id: "mug-black",
    name: "Vidyon Black Mug",
    price: 12.0,
    icon: Gift,
    iconClassName: "bg-stone-900 text-white",
    categoryId: "merch",
  },
  {
    id: "mug-white",
    name: "Vidyon White Mug",
    price: 10.0,
    icon: Gift,
    iconClassName: "bg-stone-100 text-stone-800",
    categoryId: "merch",
  },
  {
    id: "tshirt",
    name: "Vidyon Barista Tee",
    price: 20.0,
    icon: Gift,
    iconClassName: "bg-sky-100 text-sky-800",
    categoryId: "merch",
  },
  {
    id: "coffee-beans",
    name: "House Blend Beans 250g",
    price: 14.5,
    icon: Coffee,
    iconClassName: "bg-amber-200 text-amber-900",
    categoryId: "merch",
  },
];

// Ürünün kendi fotoğrafı yoksa kategorisinin fotoğrafına düşer
const categoryImageById = new Map(categories.map((c) => [c.id, c.image]));

export function getProductImage(product: Product): string {
  return product.image ?? categoryImageById.get(product.categoryId) ?? categories[0].image;
}

export interface OrderItem {
  productId: string;
  name: string;
  price: number;
  quantity: number;
}

export const initialOrderItems: OrderItem[] = [
  { productId: "espresso", name: "Espresso", price: 2.5, quantity: 2 },
  { productId: "cappuccino", name: "Cappuccino", price: 3.5, quantity: 1 },
  { productId: "croissant", name: "Croissant", price: 2.75, quantity: 1 },
  { productId: "cheesecake", name: "Cheesecake", price: 4.5, quantity: 1 },
];

export const TAX_RATE = 0.08;
